import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Calendar } from "lucide-react";
import { useTheme } from "../ThemeContext";
import { Button } from "../ui/button";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";

/**
 * Persistent demo CTA that slides up from the bottom once the hero's own buttons have scrolled away,
 * and steps aside when the page's closing CTA section is reached.
 */
export function StickyDemoCTA() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const reduceMotion = useReducedMotion();
    const [show, setShow] = useState(false);

    useEffect(() => {
        let frame = 0;
        const check = () => {
            frame = 0;
            const heroCta = document.querySelector("[data-hero-cta]");
            const closing = document.getElementById("contact");
            if (!heroCta || !closing) return;
            const pastHero = heroCta.getBoundingClientRect().bottom < 72; // under the fixed header
            const closingReached = closing.getBoundingClientRect().top < window.innerHeight * 0.9;
            setShow(pastHero && !closingReached);
        };
        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(check);
        };
        check();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ y: reduceMotion ? 0 : "120%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: reduceMotion ? 0 : "120%", opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 30 }}
                    className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-6"
                >
                    <div
                        className={`pointer-events-auto flex w-full max-w-md items-center justify-between gap-3 rounded-full border py-2 pl-5 pr-2 shadow-2xl backdrop-blur-xl md:max-w-lg ${dark
                            ? "border-purple-500/30 bg-zinc-950/85 shadow-black/60"
                            : "border-purple-200 bg-white/90 shadow-purple-500/20"
                            }`}
                    >
                        <span className={`text-sm font-medium leading-tight ${dark ? "text-slate-200" : "text-slate-700"}`}>
                            Ready to see Quilt?
                        </span>
                        <DemoRequestDialog triggerText="Schedule a Demo" source="provider_schedule_demo">
                            <Button className="btn-primary h-11 shrink-0 rounded-full px-5 text-white">
                                <Calendar className="mr-2 h-4 w-4" />
                                Schedule a Demo
                            </Button>
                        </DemoRequestDialog>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default StickyDemoCTA;
